import { useCallback, useMemo, useState } from "react";

/**
 * Transform a field name into a valid key.
 *
 * Transformation logic:
 * 1. Convert to lowercase
 * 2. Replace spaces and hyphens with underscores
 * 3. Remove all characters except a-z, 0-9, underscore
 * 4. Truncate to maxLength if provided
 *
 * @example
 * "Member-Ship Tier #01!" → "member_ship_tier_01"
 * "Customer Score" → "customer_score"
 */
export const transformNameToKey = (
  name: string,
  maxLength?: number,
): string => {
  let key = name
    // Convert to lowercase
    .toLowerCase()
    // Replace spaces and hyphens with underscores
    .replace(/[\s-]/g, "_")
    // Remove all characters except a-z, 0-9, underscore
    .replace(/[^a-z0-9_]/g, "");

  // Truncate to maxLength if provided
  if (maxLength !== undefined && key.length > maxLength) {
    key = key.slice(0, maxLength);
  }

  return key;
};

interface UseAutoGenerateKeyReturn {
  /**
   * Whether auto-generation is enabled.
   * When enabled, changing the name will auto-update the key.
   */
  isAutoGenerateEnabled: boolean;
  /**
   * Disable auto-generation (called when user manually edits the key).
   */
  disableAutoGenerate: () => void;
  /**
   * Reset auto-generation state (called when form is reset).
   */
  resetAutoGenerate: () => void;
  /**
   * Generate a key from the given name.
   * Returns the transformed key if auto-generation is enabled, null otherwise.
   */
  generateKey: (name: string) => string | null;
}

/**
 * Hook to manage auto-generation of field key from field name.
 *
 * The key is auto-generated from the name until the user manually edits it.
 * Once the user edits the key, auto-generation is disabled.
 */
export const useAutoGenerateKey = (): UseAutoGenerateKeyReturn => {
  const [isAutoGenerateEnabled, setIsAutoGenerateEnabled] = useState(true);

  const disableAutoGenerate = useCallback(() => {
    setIsAutoGenerateEnabled(false);
  }, []);

  const resetAutoGenerate = useCallback(() => {
    setIsAutoGenerateEnabled(true);
  }, []);

  const generateKey = useCallback(
    (name: string): string | null => {
      if (!isAutoGenerateEnabled) {
        return null;
      }
      return transformNameToKey(name);
    },
    [isAutoGenerateEnabled],
  );

  return useMemo(
    () => ({
      isAutoGenerateEnabled,
      disableAutoGenerate,
      resetAutoGenerate,
      generateKey,
    }),
    [
      isAutoGenerateEnabled,
      disableAutoGenerate,
      resetAutoGenerate,
      generateKey,
    ],
  );
};
